<div class="surface-card p-6">
  <!-- Header -->
  <div class="flex items-center justify-between mb-6">
    <div>
      <h2 class="text-text-primary text-xl font-semibold mb-1">{{ 'seekerDashboard.profile.title' | translate }}</h2>
      <p class="text-text-secondary text-sm">{{ 'seekerDashboard.profile.subtitle' | translate }}</p>
    </div>
    <div class="w-12 h-12 flex items-center justify-center rounded-xl bg-surface-elevated text-primary border border-border">
      <i class="fa-solid fa-user text-xl"></i>
    </div>
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="flex justify-center py-16">
    <i class="fas fa-spinner fa-spin text-3xl text-primary"></i>
  </div>

  <!-- Error -->
  <div *ngIf="error" class="alert alert-danger max-w-md mx-auto items-center justify-between text-center rounded-2xl shadow-elevated mb-4">
    {{ error }}
  </div>

  <!-- Success -->
  <div *ngIf="successMsg"
    class="alert alert-success max-w-md mx-auto items-center justify-center text-center rounded-2xl shadow-elevated mb-4 flex gap-2">
    <i class="fas fa-check-circle"></i> {{ successMsg }}
  </div>

  <form *ngIf="!loading" (ngSubmit)="save()" class="space-y-6">
    <!-- Avatar -->
    <div class="flex items-center gap-5">
      <div class="relative">
        <div
          class="w-20 h-20 rounded-full overflow-hidden bg-surface-elevated border-2 border-border flex items-center justify-center">
          <img *ngIf="avatarUrl" [src]="avatarUrl" alt="Avatar" class="w-full h-full object-cover" />
          <i *ngIf="!avatarUrl" class="fas fa-user text-text-muted text-2xl"></i>
        </div>
        <label
          class="absolute -bottom-1 -right-1 w-7 h-7 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary-hover shadow-glow transition-colors">
          <i class="fas fa-camera text-white text-xs"></i>
          <input type="file" accept="image/*" class="hidden" (change)="onImageChange($event)" />
        </label>
      </div>
      <div>
        <p class="font-semibold text-text-primary">{{ fullName || ('seekerDashboard.profile.yourName' | translate) }}</p>
        <p class="text-sm text-text-muted">{{ profile?.email }}</p>
        <p class="text-xs text-text-muted mt-0.5 capitalize font-mono tracking-wider">
          {{ profile?.role?.replace('_', ' ') }}
        </p>
      </div>
    </div>

    <!-- Personal Info -->
    <div>
      <h3 class="text-sm font-semibold text-text-primary mb-3 pb-2 border-b border-border-subtle uppercase tracking-wider font-mono">
        {{ 'seekerDashboard.profile.personalInfo' | translate }}
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm text-text-secondary mb-1">{{ 'seekerDashboard.profile.firstName' | translate }}</label>
          <input type="text" [(ngModel)]="firstName" name="firstName"
            class="form-input w-full px-4 py-2.5" />
        </div>
        <div>
          <label class="block text-sm text-text-secondary mb-1">{{ 'seekerDashboard.profile.lastName' | translate }}</label>
          <input type="text" [(ngModel)]="lastName" name="lastName"
            class="form-input w-full px-4 py-2.5" />
        </div>
        <div>
          <label class="block text-sm text-text-secondary mb-1">{{ 'seekerDashboard.profile.phoneNumber' | translate
            }}</label>
          <input type="tel" [(ngModel)]="phoneNumber" name="phoneNumber"
            class="form-input w-full px-4 py-2.5" />
        </div>

        <div>
          <label class="block text-sm text-text-secondary mb-1">{{ 'seekerDashboard.profile.language' | translate }}</label>
          <select [(ngModel)]="lang" name="lang"
            class="form-select w-full px-4 py-2.5">
            <option *ngFor="let l of langs" [value]="l" class="uppercase">{{ l }}</option>
          </select>
        </div>

        <div class="md:col-span-2">
          <label class="block text-sm text-text-secondary mb-1">{{ 'seekerDashboard.profile.aboutMe' | translate }}</label>
          <textarea [(ngModel)]="aboutMe" name="aboutMe" rows="3"
            class="form-input w-full px-4 py-2.5 resize-none"></textarea>
        </div>
      </div>
    </div>

    <!-- Privacy -->
    <div>
      <h3 class="text-sm font-semibold text-text-primary mb-3 pb-2 border-b border-border-subtle uppercase tracking-wider font-mono">
        {{ 'seekerDashboard.profile.privacy' | translate }}
      </h3>
      <div>
        <label class="block text-sm text-text-secondary mb-1">{{ 'seekerDashboard.profile.profileVisibility' | translate
          }}</label>
        <select [(ngModel)]="showProfile" name="showProfile"
          class="form-select w-full md:w-64 px-4 py-2.5">
          <option *ngFor="let opt of showProfileOptions" [value]="opt" class="capitalize">
            {{ opt }}
          </option>
        </select>
      </div>
    </div>

    <!-- Notifications -->
    <div>
      <h3 class="text-sm font-semibold text-text-primary mb-3 pb-2 border-b border-border-subtle uppercase tracking-wider font-mono">
        {{ 'seekerDashboard.profile.notifications' | translate }}
      </h3>
      <div class="flex flex-wrap gap-6">
        <label class="flex items-center gap-3 cursor-pointer group">
          <div class="relative">
            <input type="checkbox" [(ngModel)]="notifEmail" name="notifEmail" class="sr-only peer" />
            <div class="w-10 h-6 bg-surface-elevated border border-border peer-checked:bg-primary peer-checked:border-primary rounded-full transition-colors shadow-inner group-hover:border-primary/50"></div>
            <div
              class="absolute top-[3px] left-[3px] w-4 h-4 bg-text-muted rounded-full shadow-sm transition-transform peer-checked:translate-x-4 peer-checked:bg-white">
            </div>
          </div>
          <span class="text-sm text-text-secondary group-hover:text-text-primary transition-colors">{{ 'seekerDashboard.profile.emailNotifications' | translate }}</span>
        </label>
        <label class="flex items-center gap-3 cursor-pointer group">
          <div class="relative">
            <input type="checkbox" [(ngModel)]="notifApp" name="notifApp" class="sr-only peer" />
            <div class="w-10 h-6 bg-surface-elevated border border-border peer-checked:bg-primary peer-checked:border-primary rounded-full transition-colors shadow-inner group-hover:border-primary/50"></div>
            <div
              class="absolute top-[3px] left-[3px] w-4 h-4 bg-text-muted rounded-full shadow-sm transition-transform peer-checked:translate-x-4 peer-checked:bg-white">
            </div>
          </div>
          <span class="text-sm text-text-secondary group-hover:text-text-primary transition-colors">{{ 'seekerDashboard.profile.appNotifications' | translate }}</span>
        </label>
      </div>
    </div>

    <!-- Property Preferences -->
    <div>
      <h3 class="text-sm font-semibold text-text-primary mb-3 pb-2 border-b border-border-subtle uppercase tracking-wider font-mono">
        {{ 'seekerDashboard.profile.propertyPreferences' | translate }}
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm text-text-secondary mb-1">{{ 'seekerDashboard.profile.expectedBudget' | translate
            }}</label>
          <input type="number" [(ngModel)]="expectedBudget" name="expectedBudget" min="0"
            class="form-input w-full px-4 py-2.5" />
        </div>
        <div>
          <label class="block text-sm text-text-secondary mb-1">{{ 'seekerDashboard.profile.preferredArea' | translate
            }}</label>
          <input type="text" [(ngModel)]="preferredArea" name="preferredArea"
            class="form-input w-full px-4 py-2.5" />
        </div>
        <div>
          <label class="block text-sm text-text-secondary mb-1">{{ 'seekerDashboard.profile.propertyType' | translate
            }}</label>
          <select [(ngModel)]="propertyType" name="propertyType"
            class="form-select w-full px-4 py-2.5">
            <option value="">--</option>
            <option *ngFor="let p of propertyTypes" [value]="p" class="capitalize">{{ p }}</option>
          </select>
        </div>
        <div>
          <label class="block text-sm text-text-secondary mb-1">{{ 'seekerDashboard.profile.purposeOfSearch' | translate
            }}</label>
          <select [(ngModel)]="purposeOfSearch" name="purposeOfSearch"
            class="form-select w-full px-4 py-2.5">
            <option value="">{{ 'seekerDashboard.profile.selectPurpose' | translate }}</option>
            <option *ngFor="let p of purposes" [value]="p" class="capitalize">{{ p }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Save -->
    <div class="flex justify-end pt-2 border-t border-border mt-6">
      <button type="submit" [disabled]="saving"
        class="btn btn-primary mt-4 px-8 py-2.5 flex items-center gap-2 text-sm shadow-glow disabled:opacity-60 disabled:shadow-none">
        <i class="fas" [ngClass]="saving ? 'fa-spinner fa-spin' : 'fa-save'"></i>
        {{ saving ? ('seekerDashboard.profile.saving' | translate) : ('seekerDashboard.profile.saveChanges' | translate)
        }}
      </button>
    </div>
  </form>
</div>